import YearFields from '../components/YearFields';
import { datingDraft, datingPayload } from '../utils/dating';
import React, { useState, useEffect } from 'react';
import { submitChangeRequest } from '../api';
import {
  fetchProducts,
  fetchDetails,
  fetchDetailTypes,
  fetchManufacturers,
  fetchStamps,
  createDetail,
} from '../api';
import SearchableSelect from '../components/SearchableSelect';
import RichTextEditor from "../components/RichTextEditor";
import PhotoUploader, { uploadButtonLabel, usePhotoUploads } from '../components/PhotoUploader';

export default function DetailForm() {
  const [typeId, setTypeId] = useState('');
  const [productId, setProductId] = useState('');
  const [manufacturerId, setManufacturerId] = useState('');
  const [serial, setSerial] = useState('');
  const [dating, setDating] = useState(() => datingDraft());
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [stampIds, setStampIds] = useState([]);
  const uploads = usePhotoUploads();
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [belongsTo, setBelongsTo] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [subtypeId, setSubtypeId] = useState('');

  const [availableSubtypes, setAvailableSubtypes] = useState([]);

  useEffect(() => {
    if (!typeId) {
      setAvailableSubtypes([]);
      setSubtypeId('');
      return;
    }

    const selectedType = types.find(t => t.id === typeId);
    if (selectedType?.subtypes?.length > 0) {
      setAvailableSubtypes(selectedType.subtypes);
    } else {
      setAvailableSubtypes([]);
      setSubtypeId('');
    }
  }, [typeId, types]);

  useEffect(() => {
    fetchDetailTypes().then(res => setTypes(res.data)).catch(console.error);
    fetchProducts().then(res => setProducts(res.data)).catch(console.error);
    fetchManufacturers().then(res => setManufacturers(res.data)).catch(console.error);
    fetchStamps().then(res => setStamps(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (!belongsTo) {
      setFilteredTypes([]);
      return;
    }
    const filtered = types.filter(t => t.belongsTo === belongsTo);
    setFilteredTypes(filtered);
    setTypeId('');
  }, [belongsTo, types]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    try { datingPayload(dating); } catch (error) { alert(error.message); return; }

    if (!belongsTo || !typeId) {
      return;
    }
    if (uploads.busy) return;
    if (uploads.failedCount && !window.confirm('Часть фото не загрузилась. Создать деталь без них?')) return;

    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    const isModerator = auth?.role === 'MODERATOR' || auth?.role === 'ADMIN';

    setSaving(true);
    try {
      await submitChangeRequest({
        action: 'CREATE',
        entityType: 'DETAIL',
        entityId: null,
        payload: {
          type: { id: typeId },
          ...(productId && { product: { id: productId } }),
          ...(manufacturerId && { manufacturer: { id: manufacturerId } }),
          ...(serial && { number: serial }),
          ...datingPayload(dating),
          ...(name && { name }),
          ...(description && { description }),
          ...(source && { source }),
          ...(stampIds.length > 0 && { stamps: stampIds.map(id => ({ id })) }),
          ...(uploads.photos.length > 0 && { photos: uploads.photos.map(photo => ({ id: photo.id })) }),
          ...(belongsTo && { belongsTo }),
          ...(subtypeId && { subtype: { id: subtypeId } }),
        }
      });
    } catch (error) {
      console.error(error);
      alert(error.message || 'Не удалось создать деталь');
      return;
    } finally {
      setSaving(false);
    }

    if (!isModerator) {
      alert('Заявка отправлена на модерацию');
    } else {
      alert('Деталь сразу добавлена (вы модератор)');
    }

    setTypeId('');
    setProductId('');
    setManufacturerId('');
    setSerial('');
    setDating(datingDraft());
    setName('');
    setDescription('');
    setSource('');
    setStampIds([]);
    uploads.reset();
    setBelongsTo('');
    setFilteredTypes([]);
    setSubmitted(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gray-900 p-6 rounded-lg text-white">
        <h2 className="text-xl font-semibold mb-2">Создать деталь</h2>
      <div className="p-4 bg-gray-800 border-l-4 border-yellow-500 rounded">
        <p className="mb-2 font-semibold text-yellow-500">Обязательные поля</p>
        <div className="space-y-3">
          <div>
            <SearchableSelect
              options={[
                { id: 'максим', name: 'Максим' },
                { id: 'станок', name: 'Станок' },
                { id: 'ЗИП', name: 'ЗИП' }
              ]}
              value={belongsTo}
              onChange={setBelongsTo}
              placeholder="— Назначение детали —"
            />
            {submitted && !belongsTo && <p className="text-red-400 text-sm mt-1">Это поле обязательно</p>}
          </div>

          {belongsTo && (
            <div>
              <SearchableSelect
                options={filteredTypes}
                value={typeId}
                onChange={setTypeId}
                placeholder="— Выберите тип —"
              />
              {submitted && !typeId && <p className="text-red-400 text-sm mt-1">Это поле обязательно</p>}
            </div>
          )}
        </div>
      </div>

      {availableSubtypes.length > 0 && (
        <SearchableSelect
          options={availableSubtypes}
          value={subtypeId}
          onChange={setSubtypeId}
          placeholder="— Разновидность (необязательно) —"
        />
      )}


      <SearchableSelect
        options={manufacturers}
        value={manufacturerId}
        onChange={setManufacturerId}
        placeholder="— Производитель —"
      />

      <input
        className="w-full p-2 rounded bg-gray-800 text-white placeholder-gray-400 border border-gray-600"
        placeholder="Серийный номер"
        value={serial}
        onChange={e => setSerial(e.target.value)}
      />

      <YearFields value={dating} onChange={setDating} />

      <input
        className="w-full p-2 rounded bg-gray-800 text-white placeholder-gray-400 border border-gray-600"
        placeholder="Название"
        value={name}
        onChange={e => setName(e.target.value)}
      />

      <div>
        <label className="block mb-1 text-sm text-gray-300">Описание</label>
        <RichTextEditor
          value={description}
          onChange={setDescription}
          placeholder="Введите описание"
        />
      </div>

      <input
        className="w-full p-2 rounded bg-gray-800 text-white placeholder-gray-400 border border-gray-600"
        placeholder="Источник"
        value={source}
        onChange={e => setSource(e.target.value)}
      />

       <SearchableSelect
              options={products}
              value={productId}
              onChange={setProductId}
              placeholder="— К изделию —"
            />


      <select
        multiple
        className="w-full p-2 rounded bg-gray-800 text-white border border-gray-600 h-40"
        value={stampIds}
        onChange={e => setStampIds(Array.from(e.target.selectedOptions).map(o => parseInt(o.value)))}
      >
        {stamps.map(s => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>

      <PhotoUploader uploads={uploads} />

      <button
        type="submit"
        disabled={uploads.busy || saving}
        className="w-full px-6 py-2 mt-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded disabled:opacity-50 disabled:cursor-wait"
      >
        {saving ? 'Сохраняем…' : uploadButtonLabel(uploads, 'Создать деталь')}
      </button>
    </form>
  );
}
